import { Fragment, useCallback, useMemo } from 'react';
import { Row, Table as TanstackTable, flexRender } from '@tanstack/react-table';
import { VariableSizeList } from 'react-window';
import {
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Flex,
    FlexProps,
    Center,
    Text,
    Icon,
    TableHeadProps,
    Spinner,
} from '@chakra-ui/react';
import icons from 'shared/icons';
import { getScrollbarWidth } from './helpers/scrollBar';
import GlobalFilter from './filters/GlobalFilter';
import { TSortable } from 'types';
import { useTranslation } from 'helpers/languages/translations';

export interface ICustomFilter {
    id: string;
    value: string;
    onChange: (val: string) => void;
    onSubmit?: () => void;
}
export interface TableNewProps<T extends object> {
    table: TanstackTable<T>;
    containerProps?: FlexProps;
    isVirtualised?: boolean;
    isPagination?: boolean;
    isHeaderFixed?: boolean;
    customFilters?: ICustomFilter[];
    manualSort?: boolean;
    sortableColumns?: {
        [key: string]: TSortable;
    };
    handleSort?: (columnId: string | undefined) => void;
    isLoading?: boolean;
}

export function DITable<T extends object>(props: TableNewProps<T>) {
    const {
        table,
        containerProps,
        isVirtualised,
        isPagination,
        isHeaderFixed = false,
        customFilters,
        manualSort,
        sortableColumns,
        handleSort,
        isLoading,
    } = props;

    const translate = useTranslation();
    const scrollBarSize = useMemo(() => getScrollbarWidth(), []);

    const rows = table.getRowModel().rows;

    const renderRow = useCallback(
        ({ index, style }: { index: number; style: any }) => {
            try {
                const row = rows[index];
                return (
                    <Tr
                        borderBottom={'1px solid'}
                        borderColor="gray.100"
                        _last={{ borderBottom: 'none' }}
                        style={style}
                        key={row.id}
                    >
                        {row.getVisibleCells().map((cell) => {
                            return (
                                <Td py="3" key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </Td>
                            );
                        })}
                    </Tr>
                );
            } catch (error) {
                console.error('Error rendering row:', error);
                return null;
            }
        },
        [rows],
    );

    const renderRecursiveRows = (rows: Row<T>[]): React.JSX.Element[] => {
        try {
            return rows.map((row) => {
                return (
                    <Fragment key={row.id}>
                        <Tr
                            borderBottom={'1px solid'}
                            borderColor="gray.100"
                            fontSize="sm"
                            _last={{ borderBottom: 'none' }}
                        >
                            {row.getVisibleCells().map((cell) => {
                                return (
                                    <Td py="3" key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                );
                            })}
                        </Tr>

                        {(row.original as any).isExpanded && row.subRows && renderRecursiveRows(row.subRows)}
                    </Fragment>
                );
            });
        } catch (error) {
            console.error('Error rendering recursive rows:', error);
            return [];
        }
    };

    const renderRows = () => {
        try {
            const neededRows = isPagination ? table.getPaginationRowModel().rows : rows;

            const renderedRows = neededRows && renderRecursiveRows(neededRows);
            if (isVirtualised) {
                return (
                    <VariableSizeList
                        height={678}
                        itemCount={rows.length}
                        itemSize={() => 80}
                        width={table.getTotalSize() + scrollBarSize}
                    >
                        {renderRow}
                    </VariableSizeList>
                );
            }

            return renderedRows;
        } catch (error) {
            console.error('Error rendering rows:', error);
            return null;
        }
    };

    const renderEmptyRows = (isEmptyCol?: boolean) => {
        return (
            <Tr>
                <Td colSpan={table.getVisibleLeafColumns().length}>
                    <Center>
                        <Text fontSize="sm">{translate(isEmptyCol ? 'table.no_cols' : 'table.no_rows')}</Text>
                    </Center>
                </Td>
            </Tr>
        );
    };

    const renderColumnFilter = (columnHeader: string, filters: ICustomFilter[]) => {
        const filter = filters.find((f) => f.id === columnHeader);
        if (filter) {
            return (
                <Flex alignItems="center">
                    <GlobalFilter
                        globalFilter={filter.value}
                        setGlobalFilter={filter.onChange}
                        submitSearch={filter.onSubmit}
                        inputGroupStyles={{ width: '100%' }}
                    />
                </Flex>
            );
        }
    };

    const containerStyle = containerProps || {};
    const SortDescIcon = icons.sortDown;
    const SortAsc = icons.sortUp;
    const SortIcon = icons.sort;
    const headerFixedProps: TableHeadProps = isHeaderFixed
        ? { position: 'sticky', bg: 'white', top: 0, zIndex: 'sticky', boxShadow: 'base' }
        : { borderBottom: '1px solid', borderColor: 'gray.100' };

    return (
        <Flex
            maxH="640px"
            overflow="auto"
            border="1px solid"
            borderColor="gray.100"
            {...containerStyle}
            direction="column"
        >
            <Table bg={'white'}>
                <Thead {...headerFixedProps}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const column = header.column;
                                const { width, minWidth } = column.columnDef.meta || {};

                                const sortIcons = {
                                    asc: SortAsc,
                                    desc: SortDescIcon,
                                    default: SortIcon,
                                };

                                const SortableColumnIcon = ({ columnId }: { columnId: string }) => {
                                    if (!sortableColumns || !sortableColumns[columnId]) {
                                        return null;
                                    }

                                    const IconType = sortIcons[sortableColumns[columnId]];

                                    return <Icon ml="8px" color="gray.400" w={4} h={4} as={IconType} />;
                                };

                                return (
                                    <Th
                                        key={column.id}
                                        style={{
                                            whiteSpace: 'nowrap',
                                            minWidth,
                                            width,
                                            height: '100%',
                                        }}
                                    >
                                        <Flex h={'100%'} justifyContent="space-between" flexDirection="column">
                                            <Flex
                                                w="100%"
                                                alignItems="center"
                                                {...(column.getCanFilter() || manualSort ? { mb: 2 } : {})}
                                                cursor={column.getCanSort() ? 'pointer' : 'auto'}
                                                {...(manualSort && column.getCanSort()
                                                    ? {
                                                          onClick: () => {
                                                              if (handleSort) handleSort(column.id);
                                                          },
                                                      }
                                                    : column.getCanSort()
                                                    ? { onClick: header.column.getToggleSortingHandler() }
                                                    : {})}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {manualSort && column.getCanSort() ? (
                                                    <SortableColumnIcon columnId={column.id} />
                                                ) : (
                                                    <>
                                                        {column.getCanSort() && !column.getIsSorted() && (
                                                            <Icon ml="8px" color="gray.400" w={4} h={4} as={SortIcon} />
                                                        )}
                                                        {column.getIsSorted() &&
                                                            (column.getIsSorted() === 'desc' ? (
                                                                <Icon
                                                                    ml="8px"
                                                                    color="gray.400"
                                                                    w={4}
                                                                    h={4}
                                                                    as={SortDescIcon}
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    ml="8px"
                                                                    color="gray.400"
                                                                    w={4}
                                                                    h={4}
                                                                    as={SortAsc}
                                                                />
                                                            ))}
                                                    </>
                                                )}
                                            </Flex>

                                            {customFilters
                                                ? renderColumnFilter(column.id, customFilters)
                                                : column.getCanFilter() && (
                                                      <Flex>
                                                          {flexRender(
                                                              header.column.columnDef.meta?.Filter,
                                                              header.getContext(),
                                                          )}
                                                      </Flex>
                                                  )}
                                        </Flex>
                                    </Th>
                                );
                            })}
                        </Tr>
                    ))}
                </Thead>
                <Tbody color="gray.700">
                    {!isLoading && (
                        <>
                            {rows.length > 0 && table.getHeaderGroups().length > 0
                                ? renderRows()
                                : renderEmptyRows(table.getAllColumns().length === 0)}
                        </>
                    )}
                </Tbody>
            </Table>
            {isLoading && (
                <Center w="100%" h="50px" bg="white">
                    <Spinner />
                </Center>
            )}
        </Flex>
    );
}
